import { stateViewsInfo } from '../../info/stateViewsInfo';
import { createStore } from '../createStore';
import { LauncherStatesData } from './launcherStatesStore';

export type SnapType = 'edge' | 'center' | 'distribution';

export interface SnapEdge {
  elementID?: string;
  elementEdge?: 'left' | 'top' | 'bottom' | 'right' | 'centerX' | 'centerY';
  handleEdge: 'left' | 'top' | 'bottom' | 'right' | 'centerX' | 'centerY';
  snapType: 'center' | 'edge' | 'distribution';
  isSameLevel: boolean;
}

export interface SnapMemory {
  frequency: number;
  lastUsed: number;
  elements: string[];
  type: SnapType;
  distance: number;
}

export interface UIData {
  // Global
  cursor: string;
  simulationPanelPos: { x: number; y: number };
  advancedLauncherStatesPanelPos: { x: number; y: number };
  advancedElementInfoPanelPos: { x: number; y: number };
  
  openModalIDs: string[];

  isDialogOpen: boolean;
  dialogData: {
    label?: string;
    children?: JSX.Element;
    options: {
      text: string;
      onClick: () => void;
      type?: 'primary' | 'secondary' | 'danger';
    }[];
    onClose?: () => void;
  }

  // Sidebar
  isSidebarOpen: boolean;
  isSidebarResizing: boolean;
  sidebarWidth: number;
  sidebarDrawerPage: 'elements' | 'assets' | 'window';
  collapseElementTree: () => void;

  // StateViewController
  isStateViewControllerFullView: boolean;
  stateViewControllerColumnLabelsActive: (string | undefined)[];
  stateViewControllerColumnLabelsHovered: (string | undefined)[];

  // Scene
  currentLauncherState: [keyof LauncherStatesData | null, string | null]; // [state, substate]
  currentStateView: keyof typeof stateViewsInfo | null;
  selectedElementIDs: string[];
  hoveredLauncherElementIDs: string[];
  elementInEditingModeID?: string;
  editorMode: 'editing' | 'simulation';
  sceneOffset: { x: number; y: number };
  sceneZoom: number;
  openPopupIDs: string[]; // Launcher popups

  snapToEdge: boolean;
  gridPreview: boolean;
  snapToGrid: boolean;
  snappingGridSize: number;
  snappingGridThreshold: number;

  snappedEdges: {
    elementID?: string;
    elementEdge?: 'left' | 'top' | 'bottom' | 'right' | 'centerX' | 'centerY';
    handleEdge: 'left' | 'top' | 'bottom' | 'right' | 'centerX' | 'centerY';
    snapType: 'center' | 'edge' | 'distribution';
    isSameLevel: boolean;
  }[];

  // Element handle
  elementHandle: {
    isVisible: boolean;
    pos: { x: number; y: number };
    width: number;
    height: number;

    isDragging: boolean;
    isResizing: boolean;
    isDraggingX: boolean;
    isDraggingY: boolean;
    isResizingX: boolean;
    isResizingY: boolean;
    isResizingReversedX: boolean;
    isResizingReversedY: boolean;
  };

  // Properties
  pseudoclass?: 'hover' | 'active' | 'focus';

  // History
  historyUndoStack: any[];
  historyRedoStack: any[];

  snapMemory: Record<string, SnapMemory>;

  // Add snapType to the interface
  snapType: SnapType;

  showAbsoluteGuides: boolean;
  showRelativeGuides: boolean;

  useDirectVisibility: boolean;
}

export const uiDefault: UIData = {
  cursor: 'default',
  simulationPanelPos: { x: 10, y: 50 },
  advancedLauncherStatesPanelPos: { x: 10, y: 50 },
  advancedElementInfoPanelPos: { x: 10, y: 50 },

  openModalIDs: [],

  isDialogOpen: false,
  dialogData: {
    options: [],
  },

  isSidebarOpen: true,
  isSidebarResizing: false,
  sidebarWidth: 300,
  sidebarDrawerPage: 'elements',
  collapseElementTree: () => {},
  isStateViewControllerFullView: false,
  stateViewControllerColumnLabelsActive: [],
  stateViewControllerColumnLabelsHovered: [],

  currentLauncherState: [null, null],
  currentStateView: 'stateViewNotInstalled',
  selectedElementIDs: [],
  hoveredLauncherElementIDs: [],
  elementInEditingModeID: undefined,
  editorMode: 'editing',
  sceneOffset: { x: 0, y: 0 },
  sceneZoom: 1,
  openPopupIDs: [],

  snapToEdge: true,
  gridPreview: false,
  snapToGrid: false,
  snappingGridSize: 50,
  snappingGridThreshold: 5,
  snappedEdges: [],

  elementHandle: {
    isVisible: false,
    pos: { x: 0, y: 0 },
    width: 0,
    height: 0,

    isDragging: false,
    isResizing: false,
    isDraggingX: false,
    isDraggingY: false,
    isResizingX: false,
    isResizingY: false,
    isResizingReversedX: false,
    isResizingReversedY: false,
  },

  pseudoclass: undefined,

  historyUndoStack: [],
  historyRedoStack: [],

  snapMemory: {},

  snapType: 'edge',

  showAbsoluteGuides: false,
  showRelativeGuides: false,

  useDirectVisibility: true,
};

export const [UIStoreProvider, useUIStore] = createStore<UIData>(uiDefault);

export const updateSnapMemory = (UIData: UIData, snap: SnapEdge): UIData => {
  const key = `${snap.elementID}-${snap.handleEdge}-${snap.elementEdge}`;
  const existing = UIData.snapMemory[key] || { 
    frequency: 0, 
    lastUsed: 0, 
    elements: [], 
    type: snap.snapType, 
    distance: 0 
  };
  
  return {
    ...UIData,
    snapMemory: {
      ...UIData.snapMemory,
      [key]: {
        ...existing,
        frequency: existing.frequency + 1,
        lastUsed: Date.now(),
      }
    }
  };
};
